/**
 * Skateboard Setup Builder - deck + trucks + wheels + bearings + grip,
 * skateboard or longboard. Same architecture as assets/setup-builder.js
 * (snowboard/boot/binding builder): linear step wizard, a stepper track,
 * a running "so far" answers sidebar, and pick-* steps that show a full
 * card grid rather than a single recommendation. Raw catalog data is
 * injected onto window.SkateBuilderRaw by sections/skate-setup-builder.liquid
 * - title/tags/vendor only, unparsed. This file does the parsing (deck
 * width from title, wheel size/durometer from title, truck-fit from the
 * "Match Your Board Size" tag, riding style from the tag map in
 * skate-setup-builder-data.js) so Liquid's job stays pure data plumbing,
 * matching the snowboard builder's split.
 *
 * Deliberately NOT a clickable board diagram - that approach (visual
 * hotspots on a skateboard illustration) was prototyped first but scrapped
 * as too cumbersome; a straight step wizard reusing the snowboard
 * builder's proven pattern is simpler to build, test, and maintain.
 *
 * Flow: board type -> style -> (skateboard only) shoe size -> pick deck ->
 * pick trucks -> truck feel (bushing/pivot-cup upsell) -> riser pads? ->
 * (pick risers if yes) -> pick hardware (bolts, length driven by the riser
 * answer; also offers an optional skate-tool add-on) -> wheel feel -> pick
 * wheels -> pick bearings (tier recommended from riding style, see
 * bearingTierByStyle) -> grip (optional upgrade) -> bundle.
 * Every pick-* step shows every catalog match in the recommended range -
 * given how many graphics/colourways exist per size, guessing a "best"
 * one is worse than letting the customer choose on looks.
 */
(function () {
  "use strict";

  // "pick-risers" is deliberately absent here - it's spliced into
  // state.steps at runtime only when the customer answers "yes" to riser
  // pads (see setRiserPref), so a "no riser" customer never sees a step
  // for a product they don't need.
  var STEPS_BY_BOARDTYPE = {
    skateboard: ["board-type", "style", "shoe-size", "pick-deck", "pick-trucks", "truck-feel", "pick-addons", "riser", "pick-hardware", "wheel-feel", "pick-wheels", "pick-bearings", "grip", "bundle"],
    longboard: ["board-type", "style", "pick-deck", "pick-trucks", "truck-feel", "pick-addons", "riser", "pick-hardware", "wheel-feel", "pick-wheels", "pick-bearings", "grip", "bundle"]
  };

  var STEP_INVALIDATES = {
    "board-type": ["answers.style", "answers.shoeSize", "answers.shoeSizeDraft", "picks.deck", "answers.existingDeckWidth", "picks.trucks", "answers.truckStiffness", "picks.bushings", "picks.pivotCups", "answers.riserPref", "picks.risers", "picks.hardware", "picks.tool", "answers.wheelFollowup", "picks.wheels", "picks.bearings", "picks.grip"],
    "style": ["picks.deck", "answers.existingDeckWidth", "picks.trucks", "answers.truckStiffness", "picks.bushings", "picks.pivotCups", "answers.riserPref", "picks.risers", "picks.hardware", "picks.tool", "answers.wheelFollowup", "picks.wheels", "picks.bearings", "picks.grip"],
    "shoe-size": ["picks.deck", "answers.existingDeckWidth", "picks.trucks", "answers.truckStiffness", "picks.bushings", "picks.pivotCups"],
    "pick-deck": ["picks.trucks", "answers.truckStiffness", "picks.bushings", "picks.pivotCups"],
    "pick-trucks": ["answers.truckStiffness", "picks.bushings", "picks.pivotCups"],
    "truck-feel": ["picks.bushings", "picks.pivotCups"],
    "pick-addons": [],
    "riser": ["picks.risers", "picks.hardware"],
    "pick-risers": [],
    "pick-hardware": [],
    "wheel-feel": ["picks.wheels", "picks.bearings"],
    "pick-wheels": [],
    "pick-bearings": [],
    "grip": []
  };

  var STEP_LABELS = {
    "board-type": "Skateboard or longboard",
    style: "Riding style",
    "shoe-size": "Shoe size",
    "pick-deck": "Your deck",
    "pick-trucks": "Your trucks",
    "truck-feel": "Turning feel",
    riser: "Riser pads?",
    "pick-risers": "Riser pads",
    "pick-hardware": "Hardware",
    "pick-addons": "Fine-tune",
    "wheel-feel": "Wheel priority",
    "pick-wheels": "Your wheels",
    "pick-bearings": "Your bearings",
    grip: "Griptape",
    bundle: "Your setup"
  };

  var SKATE_STYLES = [
    { value: "street", label: "Street" },
    { value: "park", label: "Park" },
    { value: "vert", label: "Vert" },
    { value: "cruiser", label: "Cruiser" },
    { value: "beginner", label: "Beginner / learning" }
  ];
  var LONGBOARD_STYLES = [
    { value: "cruising", label: "Cruising" },
    { value: "downhill", label: "Downhill" },
    { value: "freeride", label: "Freeride" },
    { value: "carving", label: "Carving" }
  ];

  /* =========================================================
     RAW DATA PARSING - turns window.SkateBuilderRaw (title/tags/vendor
     only, as output by the Liquid section) into the shape the wizard UI
     below expects. Kept separate from the render/navigation code so the
     matching rules can be audited/adjusted without touching UI logic.
     ========================================================= */


  // Real catalog titles/tags mix straight quotes (") and curly/smart
  // quotes/prime marks for inches -- confirmed live on this exact catalog:
  // one product title uses a straight quote, another (same collection)
  // uses a curly one. Dashes vary too (hyphen vs en dash) in tag data
  // entered over time by different people. Every parser below runs
  // input through this first so a customer's match doesn't silently
  // fail on a typographic difference no one would notice by eye.
  //
  // Written with \u escape sequences rather than literal curly-quote/
  // dash characters on purpose -- literal non-ASCII characters in
  // source code are exactly the kind of thing that can get silently
  // mangled going through a chat message, a clipboard, and a
  // browser-based code editor. \u escapes are plain ASCII, so there is
  // nothing for that pipeline to corrupt.
  function normalizeQuotesAndDashes(s) {
    return s
      .replace(/[\u2018\u2019\u2032]/g, "'")
      .replace(/[\u201C\u201D\u2033]/g, '"')
      .replace(/[\u2013\u2014-]/g, "-");
  }

  function parseTrailingInches(title) {
    var norm = normalizeQuotesAndDashes(title);
    var matches = norm.match(/(\d+(?:\.\d+)?)\s*"/g);
    if (!matches || !matches.length) return null;
    return parseFloat(matches[matches.length - 1]);
  }

  function parseWheelSpec(title) {
    var norm = normalizeQuotesAndDashes(title);
    // "56mm x 40mm x 88A" - diameter, contact width (unused), durometer.
    var m = norm.match(/(\d+(?:\.\d+)?)\s*mm\s*x\s*\d+(?:\.\d+)?\s*mm\s*x\s*(\d+(?:\.\d+)?)\s*a\b/i);
    if (m) return { size: parseFloat(m[1]), durometer: parseFloat(m[2]) };
    // "56mm / 85a" or "52mm 99A" - diameter, durometer, nothing between.
    m = norm.match(/(\d+(?:\.\d+)?)\s*mm[^0-9]{0,6}(\d+(?:\.\d+)?)\s*a\b/i);
    if (m) return { size: parseFloat(m[1]), durometer: parseFloat(m[2]) };
    return null;
  }

  // Hardware/riser titles use imperial fractions ("1 1/8"", "7/8"") as
  // often as plain decimals ("1""), unlike deck widths which are always
  // decimal - parseTrailingInches alone would misread "1/8"" as 8. Fraction
  // patterns are checked first since they're more specific; the decimal
  // fallback only applies once those don't match.
  function parseImperialInches(title) {
    var norm = normalizeQuotesAndDashes(title);
    var m = norm.match(/(\d+)[\s-](\d+)\/(\d+)\s*"/);
    if (m) return parseInt(m[1], 10) + (parseInt(m[2], 10) / parseInt(m[3], 10));
    m = norm.match(/(\d+)\/(\d+)\s*"/);
    if (m) return parseInt(m[1], 10) / parseInt(m[2], 10);
    return parseTrailingInches(title);
  }

  function parseMatchBoardSizeTags(tags) {
    var ranges = [];
    (tags || []).forEach(function (t) {
      var norm = normalizeQuotesAndDashes(t.trim());
      var m = norm.match(/^Match Your Board Size_\d+~\s*(\d+(?:\.\d+)?)"\s*-\s*(\d+(?:\.\d+)?)"$/i);
      if (m) ranges.push([parseFloat(m[1]), parseFloat(m[2])]);
    });
    return ranges;
  }

  function parseSizeMmTag(tags) {
    var found = null;
    (tags || []).forEach(function (t) {
      var m = normalizeQuotesAndDashes(t.trim()).match(/^Size_\d+~(\d+)mm-(\d+)mm$/i);
      if (m) found = [parseFloat(m[1]), parseFloat(m[2])];
    });
    return found;
  }

  function hasTag(tags, tagString) {
    var target = tagString.trim().toLowerCase();
    return (tags || []).some(function (t) { return t.trim().toLowerCase() === target; });
  }

  function stylesFromTags(tags, map) {
    var out = [];
    Object.keys(map).forEach(function (style) { if (hasTag(tags, map[style])) out.push(style); });
    return out;
  }

  function parseKingpinType(tags) {
    if (hasTag(tags, "Style_Reverse Kingpin")) return "Reverse kingpin";
    if (hasTag(tags, "Style_Double Kingpin")) return "Double kingpin";
    if (hasTag(tags, "Style_Standard")) return "Standard kingpin";
    return null;
  }

  function bearingTierFromPrice(cents, cfg) {
    var t = cfg.bearingPriceThresholdsCents;
    if (cents <= t.entryMax) return "entry";
    if (cents <= t.midMax) return "mid";
    return "premium";
  }

  function parseBushingTier(title, cfg) {
    var band = cfg.bushingDurometerBands;
    var numMatch = title.match(/(\d+(?:\.\d+)?)\s*a\b/i);
    if (numMatch) {
      var d = parseFloat(numMatch[1]);
      if (d <= band.softMax) return "soft";
      if (d <= band.mediumMax) return "medium";
      return "stiff";
    }
    var lower = title.toLowerCase();
    if (lower.indexOf("soft") >= 0) return "soft";
    if (lower.indexOf("medium") >= 0) return "medium";
    if (lower.indexOf("stiff") >= 0 || lower.indexOf("hard") >= 0) return "stiff";
    return "medium";
  }

  function buildCatalog(raw, cfg) {
    raw = raw || {};

    function base(p) {
      return { id: p.id, variantId: p.variantId, title: p.title, price: p.price, image: p.image, url: p.url, available: p.available !== false };
    }

    function normalizeDeck(p, boardType) {
      var out = base(p);
      out.boardType = boardType;
      var trailing = parseTrailingInches(p.title);
      if (boardType === "skateboard") {
        out.width = trailing;
        out.styleTags = stylesFromTags(p.tags, cfg.styleTagMap.skateDecks);
      } else {
        out.lengthIn = trailing;
        out.styleTags = stylesFromTags(p.tags, cfg.styleTagMap.longboardDecks);
      }
      return out;
    }

    function normalizeSkateTruck(p) {
      var out = base(p);
      out.boardType = "skateboard";
      out.brand = p.vendor;
      out.matchBoardSizeRanges = parseMatchBoardSizeTags(p.tags);
      return out;
    }

    function normalizeLongboardTruck(p) {
      var out = base(p);
      out.boardType = "longboard";
      out.styleTags = stylesFromTags(p.tags, cfg.styleTagMap.longboardTrucks);
      out.widthRangeMm = parseSizeMmTag(p.tags);
      out.kingpinType = parseKingpinType(p.tags);
      return out;
    }

    function normalizeWheel(p, boardType) {
      var out = base(p);
      out.boardType = boardType;
      var spec = parseWheelSpec(p.title) || {};
      out.size = spec.size || null;
      out.durometer = spec.durometer || null;
      return out;
    }

    function normalizeBearing(p) {
      var out = base(p);
      out.tier = bearingTierFromPrice(p.price, cfg);
      return out;
    }

    function normalizeBushing(p) {
      var out = base(p);
      out.tier = parseBushingTier(p.title, cfg);
      return out;
    }

    function normalizeGrip(p, kind) {
      var out = base(p);
      out.kind = kind;
      // Standard grip genuinely ships free with every deck - zero the
      // price so it reads "Free" (not the underlying product's real
      // price) and, per addAllToCart below, it's excluded from checkout
      // entirely rather than added as its own free-but-still-real line
      // item.
      if (kind === "standard") out.price = 0;
      return out;
    }

    function normalizeHardware(p, boardType) {
      var out = base(p);
      out.boardType = boardType;
      out.length = parseImperialInches(p.title);
      return out;
    }

    var decksSkate = (raw.decks && raw.decks.skateboard || []).map(function (p) { return normalizeDeck(p, "skateboard"); }).filter(function (d) { return d.available && d.width; });
    var decksLong = (raw.decks && raw.decks.longboard || []).map(function (p) { return normalizeDeck(p, "longboard"); }).filter(function (d) { return d.available; });
    var trucksSkate = (raw.trucks && raw.trucks.skateboard || []).map(normalizeSkateTruck).filter(function (t) { return t.available; });
    var trucksLong = (raw.trucks && raw.trucks.longboard || []).map(normalizeLongboardTruck).filter(function (t) { return t.available; });
    var wheelsSkate = (raw.wheels && raw.wheels.skateboard || []).map(function (p) { return normalizeWheel(p, "skateboard"); }).filter(function (w) { return w.available && w.size && w.durometer; });
    var wheelsLong = (raw.wheels && raw.wheels.longboard || []).map(function (p) { return normalizeWheel(p, "longboard"); }).filter(function (w) { return w.available && w.size && w.durometer; });
    var bearings = (raw.bearings || []).map(normalizeBearing).filter(function (b) { return b.available; });

    // Pivot cups share the "Bushings" tag with real bushings on the live
    // catalog (confirmed in METADATA.md) - split them out by title so the
    // Bushings collection setting doesn't need a second, redundant tag.
    var bushingsRaw = (raw.bushings || []).filter(function (p) { return !/pivot cup/i.test(p.title); });
    var bushings = bushingsRaw.map(normalizeBushing).filter(function (b) { return b.available; });
    // Pivot cup upsell disabled until real stock is back (all known pivot
    // cup products are 0/negative stock as of Aug 2026) - force null here
    // rather than removing the feature outright, so re-enabling later is a
    // one-line revert instead of rebuilding the upsell.
    var pivotCups = null;

    var grip = [];
    if (raw.griptapeStandard) grip.push(normalizeGrip(raw.griptapeStandard, "standard"));
    (raw.griptapeUpgrades || []).forEach(function (p) { grip.push(normalizeGrip(p, "patterned")); });

    // Risers are board-type split on the live catalog (separate "risers"
    // / "risers-longboarding" collections, confirmed live) - same split
    // as hardware, not the single shared collection originally assumed.
    var risersSkate = (raw.risers && raw.risers.skateboard || []).map(base).filter(function (r) { return r.available; });
    var risersLong = (raw.risers && raw.risers.longboard || []).map(base).filter(function (r) { return r.available; });
    var hardwareSkate = (raw.hardware && raw.hardware.skateboard || []).map(function (p) { return normalizeHardware(p, "skateboard"); }).filter(function (h) { return h.available; });
    var hardwareLong = (raw.hardware && raw.hardware.longboard || []).map(function (p) { return normalizeHardware(p, "longboard"); }).filter(function (h) { return h.available; });
    var tool = raw.toolFeatured ? base(raw.toolFeatured) : null;

    return {
      decks: { skateboard: decksSkate, longboard: decksLong },
      trucks: { skateboard: trucksSkate, longboard: trucksLong },
      wheels: { skateboard: wheelsSkate, longboard: wheelsLong },
      bearings: bearings, bushings: bushings, pivotCups: pivotCups, grip: grip,
      risers: { skateboard: risersSkate, longboard: risersLong },
      hardware: { skateboard: hardwareSkate, longboard: hardwareLong }, tool: tool
    };
  }

  /* =========================================================
     WIZARD - everything below reads from the normalized `catalog` object
     built above; no raw title/tag parsing happens past this point.
     ========================================================= */

  function init(root) {
    var cfg = window.SkateBuilderConfig || {};
    var catalog = buildCatalog(window.SkateBuilderRaw, cfg);
    var decks = catalog.decks, trucks = catalog.trucks, wheels = catalog.wheels;
    var bearings = catalog.bearings, bushings = catalog.bushings, pivotCups = catalog.pivotCups, grip = catalog.grip;
    var risers = catalog.risers, hardware = catalog.hardware, tool = catalog.tool;

    var state = {
      started: false,
      stepIndex: 0,
      maxReached: 0,
      steps: ["board-type"],
      answers: {
        boardType: null,
        style: null,
        shoeSize: null,
        shoeSizeDraft: null,
        shoeScale: "mens",
        truckStiffness: null,
        riserPref: null,
        wheelFollowup: {},
        // Width of a deck the customer already owns, entered when skipping
        // the deck step - the only skip with a real downstream dependency
        // (skateboard truck matching needs a width). Nothing else in the
        // flow depends on an earlier pick enough to need this treatment.
        existingDeckWidth: null
      },
      picks: { deck: null, trucks: null, wheels: null, bearings: null, grip: null, bushings: null, pivotCups: null, risers: null, hardware: null, tool: null },
      ui: { skipPromptOpenFor: null }
    };

    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }
    function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
    function formatPrice(cents) { return cents ? "$" + (cents / 100).toFixed(2) : "Free"; }
    function round2(n) { return Math.round(n * 100) / 100; }

    function render() {
      root.innerHTML = "";
      if (!state.started) { root.appendChild(renderWelcome()); return; }

      var name = state.steps[state.stepIndex];
      if (name === "board-type") { root.appendChild(renderStep(name)); return; }

      var layout = el("div", "sb-layout");
      var summary = renderAnswerSummary();
      if (summary) layout.appendChild(summary);

      var main = el("div", "sb-layout__main");
      main.appendChild(renderStepper());
      main.appendChild(name === "bundle" ? renderBundle() : renderStep(name));
      layout.appendChild(main);
      root.appendChild(layout);
    }

    function renderWelcome() {
      var panel = el("div", "sb-panel sb-welcome");
      panel.appendChild(el("h3", "sb-step__title", "Build your setup"));
      panel.appendChild(el("p", "sb-step__hint", "A few quick questions, then we'll show you every deck, truck, wheel and bearing that actually fits - you pick on looks."));
      var btn = el("button", "sb-btn sb-btn--primary", "Start building");
      btn.type = "button";
      btn.addEventListener("click", function () { state.started = true; render(); });
      panel.appendChild(btn);
      return panel;
    }

    function renderAnswerSummary() {
      var a = state.answers, p = state.picks, parts = [];
      if (a.boardType) parts.push(capitalize(a.boardType));
      if (a.style) parts.push(capitalize(a.style));
      if (a.shoeSize) parts.push((a.shoeScale === "womens" ? "US W" : a.shoeScale === "kids" ? "US Kids" : "US M") + " " + a.shoeSize);
      if (p.deck) parts.push(p.deck.title);
      else if (a.existingDeckWidth) parts.push(a.existingDeckWidth + "\u2033 deck (yours)");
      if (p.trucks) parts.push(p.trucks.title);
      if (a.truckStiffness) parts.push(capitalize(a.truckStiffness) + " turn");
      if (p.bushings) parts.push(p.bushings.title);
      if (p.pivotCups) parts.push(p.pivotCups.title);
      if (p.risers) parts.push(p.risers.title);
      if (p.hardware) parts.push(p.hardware.title);
      if (p.tool) parts.push(p.tool.title);
      if (p.wheels) parts.push(p.wheels.title);
      if (p.bearings) parts.push(p.bearings.title);
      if (p.grip) parts.push(p.grip.title);
      if (!parts.length) return null;
      var wrap = el("div", "sb-answer-summary");
      // Shown once a deck is picked so the customer can eyeball graphic/colour
      // matches against wheels, grip, etc. later in the flow without having
      // to scroll back - most useful on the wheels step, but left visible
      // throughout since it's cheap and never wrong to show.
      if (p.deck && p.deck.image) {
        var deckImg = document.createElement("img");
        deckImg.className = "sb-answer-summary__deck-image";
        deckImg.src = p.deck.image;
        deckImg.alt = p.deck.title;
        wrap.appendChild(deckImg);
      }
      wrap.appendChild(el("div", "sb-answer-summary__label", "So far"));
      var list = el("div", "sb-answer-summary__list");
      parts.forEach(function (t) { list.appendChild(el("div", "sb-answer-summary__item", t)); });
      wrap.appendChild(list);
      return wrap;
    }

    function renderStepper() {
      var wrap = el("div", "sb-stepper");
      var track = el("div", "sb-stepper__track");
      state.steps.forEach(function (name, i) {
        var done = i < state.maxReached;
        var current = i === state.stepIndex;
        var clickable = done && !current;
        var dot = el("button", "sb-stepper__dot" + (current ? " is-current" : "") + (done ? " is-done" : "") + (clickable ? " is-clickable" : ""), String(i + 1));
        dot.type = "button";
        dot.setAttribute("aria-label", STEP_LABELS[name] || name);
        if (current) dot.setAttribute("aria-current", "step");
        if (clickable) { dot.addEventListener("click", function () { goToStep(i); }); } else { dot.disabled = true; }
        track.appendChild(dot);
      });
      wrap.appendChild(track);
      wrap.appendChild(el("div", "sb-stepper__label", STEP_LABELS[state.steps[state.stepIndex]] || ""));
      return wrap;
    }

    function renderStep(name) {
      var isPicker = name.indexOf("pick-") === 0 || name === "grip";
      var panel = el("div", isPicker ? "sb-panel sb-panel--wide" : "sb-panel");
      var body = {
        "board-type": boardTypeStep,
        style: styleStep, "shoe-size": shoeSizeStep, "pick-deck": pickDeckStep,
        "pick-trucks": pickTrucksStep, "truck-feel": truckFeelStep, "pick-addons": pickAddonsStep,
        riser: riserStep, "pick-risers": pickRisersStep, "pick-hardware": pickHardwareStep,
        "wheel-feel": wheelFeelStep, "pick-wheels": pickWheelsStep,
        "pick-bearings": pickBearingsStep, grip: gripStep
      }[name]();
      panel.appendChild(body);
      return panel;
    }

    function confirmBoardType(value) {
      state.answers.boardType = value;
      // .slice() - state.steps gets spliced in place (see setRiserPref),
      // so it must be this instance's own array, not a shared reference
      // back to the STEPS_BY_BOARDTYPE template that every future
      // board-type switch (and every other instance on the page) also
      // reads from.
      state.steps = STEPS_BY_BOARDTYPE[value].slice();
      state.stepIndex = 1;
      state.maxReached = Math.max(state.maxReached, 1);
      render();
    }

    function confirmStep(stepIdx, patch) {
      Object.keys(patch).forEach(function (k) { state.answers[k] = patch[k]; });
      advanceFrom(stepIdx);
    }

    function confirmPick(stepIdx, pickKey, product) {
      state.picks[pickKey] = product;
      advanceFrom(stepIdx);
    }

    function advanceFrom(stepIdx) {
      state.stepIndex = stepIdx + 1;
      state.maxReached = Math.max(state.maxReached, state.stepIndex);
      state.ui.skipPromptOpenFor = null;
      render();
    }

    function goToStep(i) {
      var name = state.steps[i];
      (STEP_INVALIDATES[name] || []).forEach(function (path) {
        var parts = path.split(".");
        state[parts[0]][parts[1]] = parts[1] === "wheelFollowup" ? {} : null;
      });
      state.stepIndex = i;
      state.maxReached = i + 1;
      state.ui.skipPromptOpenFor = null;
      render();
    }

    function showError(container, message) {
      var existing = container.querySelector(".sb-error");
      if (existing) existing.remove();
      container.appendChild(el("div", "sb-error", message));
    }

    function boardTypeStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Skateboard or longboard?"));
      f.appendChild(el("p", "sb-step__hint", "Catches longboard setups in the same tool - the questions and matching logic differ from here."));
      var group = el("div", "sb-options");
      [["skateboard", "Skateboard"], ["longboard", "Longboard"]].forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.boardType === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () { confirmBoardType(opt[0]); });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    function styleStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "What's your riding style?"));
      var options = state.answers.boardType === "longboard" ? LONGBOARD_STYLES : SKATE_STYLES;
      var group = el("div", "sb-options");
      options.forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.style === opt.value ? " is-active" : ""), opt.label);
        btn.type = "button";
        btn.addEventListener("click", function () {
          confirmStep(state.steps.indexOf("style"), { style: opt.value });
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    function shoeSizeStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "What's your shoe size?"));
      f.appendChild(el("p", "sb-step__hint", "Drives the deck width we recommend, alongside your riding style."));

      var toggle = el("div", "sb-toggle");
      ["mens", "womens", "kids"].forEach(function (scale) {
        var btn = el("button", "sb-toggle__btn" + (state.answers.shoeScale === scale ? " is-active" : ""), scale === "mens" ? "Men's" : scale === "womens" ? "Women's" : "Kids");
        btn.type = "button";
        btn.addEventListener("click", function () { state.answers.shoeScale = scale; render(); });
        toggle.appendChild(btn);
      });
      f.appendChild(toggle);

      var input = el("input", "sb-input");
      input.type = "number"; input.step = "0.5"; input.min = "1"; input.max = "16";
      input.placeholder = "US size, e.g. 9";
      if (state.answers.shoeSizeDraft != null) input.value = state.answers.shoeSizeDraft;
      else if (state.answers.shoeSize) input.value = state.answers.shoeSize;
      input.addEventListener("input", function () { state.answers.shoeSizeDraft = input.value; });
      f.appendChild(input);

      var btn = el("button", "sb-btn sb-btn--primary", "Continue");
      btn.type = "button";
      btn.addEventListener("click", function () {
        var val = parseFloat(input.value);
        if (!val || val <= 0) { showError(f, "Enter a shoe size to continue."); return; }
        confirmStep(state.steps.indexOf("shoe-size"), { shoeSize: val });
      });
      f.appendChild(btn);
      return f;
    }

    function mensEquivalentShoe() {
      var a = state.answers;
      return a.shoeScale === "womens" ? a.shoeSize - 2 : a.shoeSize;
    }

    function recommendDeckWidthRange() {
      var a = state.answers;
      var table = cfg.deckWidthByShoeSize;
      var mens = mensEquivalentShoe();
      var base = table[table.length - 1].width;
      for (var i = 0; i < table.length; i++) { if (mens <= table[i].maxSize) { base = table[i].width; break; } }
      var lo = base[0], hi = base[1];
      var skew = cfg.deckWidthStyleSkew;
      if (skew.narrow.indexOf(a.style) >= 0) hi = Math.min(hi, lo + 0.5);
      if (skew.wide.indexOf(a.style) >= 0) { lo = Math.max(lo, hi - 0.5); hi += 0.25; }
      return [round2(lo), round2(hi)];
    }

    function pickDeckStep() {
      var a = state.answers;
      var pool = decks[a.boardType];
      var list, range = null;
      if (a.boardType === "skateboard") {
        range = recommendDeckWidthRange();
        list = pool.filter(function (d) { return d.width >= range[0] && d.width <= range[1]; });
        if (!list.length) {
          list = pool.slice().sort(function (x, y) {
            return Math.abs(x.width - (range[0] + range[1]) / 2) - Math.abs(y.width - (range[0] + range[1]) / 2);
          }).slice(0, 3);
        } else {
          list.sort(function (x, y) { return x.width - y.width; });
        }
      } else {
        list = pool.filter(function (d) { return (d.styleTags || []).indexOf(a.style) >= 0; });
        if (!list.length) list = pool;
      }

      var banner = range
        ? "Recommended width: <strong>" + range[0] + "\u2033-" + range[1] + "\u2033</strong> for a " + (a.shoeScale === "womens" ? "US W" : "US M") + " " + a.shoeSize + " / " + capitalize(a.style) + " rider. Showing all " + list.length + " decks in that range."
        : "Showing all " + list.length + " decks for " + capitalize(a.style) + " riding.";

      // Skateboard-only: skipping the deck loses the one thing later steps
      // actually need from it (a width to match trucks against), so ask
      // for it directly rather than falling back to "show everything."
      var skipQuestion = a.boardType === "skateboard" ? {
        label: "What width is your current deck? (inches - optional, but it means we can still match trucks to it)",
        placeholder: "e.g. 8.0",
        onConfirm: function (val) { state.answers.existingDeckWidth = val; }
      } : null;

      return renderPicks("Pick your deck", list, banner, "pick-deck", "deck", function (card, d) {
        buildProductCardBody(card, d, d.boardType === "skateboard" ? d.width + "\u2033 wide" : (d.lengthIn ? d.lengthIn + "\u2033 long" : ""));
      }, "No decks matched - showing the closest available widths instead.", skipQuestion);
    }

    function pickTrucksStep() {
      var a = state.answers;
      var list, banner;
      if (a.boardType === "skateboard") {
        // state.picks.deck can be null - deck is skippable like everything
        // else now, e.g. a customer who already has a deck and just wants
        // matching trucks. Falls back to the width they typed in on the
        // deck-skip prompt, if they gave one; only shows everything
        // unfiltered as a last resort with no width at all.
        var deckWidth = state.picks.deck ? state.picks.deck.width : a.existingDeckWidth;
        if (deckWidth == null) {
          list = trucks.skateboard;
          banner = "No deck width to match against yet - showing all " + list.length + " trucks we stock.";
          return renderPicks("Pick your trucks", list, banner, "pick-trucks", "trucks", function (card, t) {
            buildProductCardBody(card, t, t.brand);
          }, "No trucks in stock right now.");
        }
        list = trucks.skateboard.filter(function (t) {
          if (t.matchBoardSizeRanges.length) {
            return t.matchBoardSizeRanges.some(function (r) { return deckWidth >= r[0] && deckWidth <= r[1]; });
          }
          // Fallback for the rare truck missing the "Match Your Board
          // Size" tag - approximate via the brand's published size chart.
          var chart = cfg.truckChartsByBrand[t.brand];
          return !!(chart && chart.some(function (r) { return deckWidth >= r.min && deckWidth <= r.max; }));
        });
        banner = "For a " + deckWidth + "\u2033 deck, showing all " + list.length + " trucks tagged to fit that width.";
        return renderPicks("Pick your trucks", list, banner, "pick-trucks", "trucks", function (card, t) {
          var band = t.matchBoardSizeRanges.filter(function (r) { return deckWidth >= r[0] && deckWidth <= r[1]; })[0];
          buildProductCardBody(card, t, band ? "Fits " + band[0] + "\u2033-" + band[1] + "\u2033 decks" : t.brand);
        }, "No trucks tagged for that deck width - call the shop to check what's in stock.");
      } else {
        list = trucks.longboard.filter(function (t) { return (t.styleTags || []).indexOf(a.style) >= 0; });
        if (!list.length) list = trucks.longboard;
        var angle = cfg.longboardAngleByStyle[a.style];
        banner = angle
          ? capitalize(a.style) + " riders usually want a " + (angle === "low" ? "lower" : "higher") + " baseplate angle for " + (angle === "low" ? "stability at speed" : "responsive turning") + " - showing all " + list.length + " matching trucks."
          : "Showing all " + list.length + " trucks for " + capitalize(a.style) + " riding.";
        return renderPicks("Pick your trucks", list, banner, "pick-trucks", "trucks", function (card, t) {
          var spec = t.kingpinType || (t.widthRangeMm ? t.widthRangeMm[0] + "-" + t.widthRangeMm[1] + "mm" : "");
          buildProductCardBody(card, t, spec);
        }, "No trucks matched that riding style - call the shop to check what's in stock.");
      }
    }

    function truckFeelStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "How do you want your trucks to turn?"));
      f.appendChild(el("p", "sb-step__hint", "Stock bushings are a generic medium - this just tells us what to recommend next, it doesn't change which trucks you picked."));
      var group = el("div", "sb-options");
      [["soft", "Soft / carvy"], ["medium", "Medium"], ["stiff", "Stiff / stable"]].forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.truckStiffness === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () {
          confirmStep(state.steps.indexOf("truck-feel"), { truckStiffness: opt[0] });
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    function pickAddonsStep() {
      var f = el("div", "sb-step sb-step--picker");
      f.appendChild(el("h3", "sb-step__title", "Fine-tune your trucks"));
      f.appendChild(el("p", "sb-step__hint", "Optional - skip if stock bushings are fine."));

      var tier = state.answers.truckStiffness || "medium";
      var recommended = bushings.filter(function (b) { return b.tier === tier; })[0];
      var grid = el("div", "sb-addon-grid");

      if (recommended) grid.appendChild(addonCard(recommended, "bushings", "recommended for \u201C" + tier + "\u201D"));
      if (pivotCups) grid.appendChild(addonCard(pivotCups, "pivotCups", "smoother, more precise turn"));
      f.appendChild(grid);

      var next = el("button", "sb-btn sb-btn--primary", "Continue");
      next.type = "button";
      next.addEventListener("click", function () { advanceFrom(state.steps.indexOf("pick-addons")); });
      f.appendChild(next);
      return f;
    }

    function addonCard(product, pickKey, metaText) {
      var selected = state.picks[pickKey] && state.picks[pickKey].id === product.id;
      var card = el("div", "sb-addon-card" + (selected ? " is-selected" : ""));
      var info = el("div");
      info.appendChild(el("div", "sb-addon-card__title", product.title));
      info.appendChild(el("div", "sb-addon-card__meta", metaText + " \u00B7 " + formatPrice(product.price)));
      card.appendChild(info);
      var btn = el("button", "sb-addon-card__toggle", selected ? "Added" : "Add");
      btn.type = "button";
      btn.addEventListener("click", function () {
        state.picks[pickKey] = selected ? null : product;
        render();
      });
      card.appendChild(btn);
      return card;
    }

    // Bolts are a required purchase - riser pads aren't, but the riser
    // answer is what actually determines which bolt length fits, so the
    // question has to come before the (required) hardware step regardless
    // of whether the customer ends up buying risers. Answering this
    // splices/removes "pick-risers" from state.steps rather than just
    // recording an answer, since "no riser" means that step shouldn't
    // exist for this customer at all.
    function riserStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Using riser pads?"));
      f.appendChild(el("p", "sb-step__hint", "Determines which hardware length actually fits - risers add height between the truck and deck."));
      var group = el("div", "sb-options");
      [["no", "No riser"], ["yes", "Yes, riser pads"]].forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.riserPref === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () { setRiserPref(opt[0]); });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    function setRiserPref(value) {
      state.answers.riserPref = value;
      var idx = state.steps.indexOf("riser");
      var riserStepIdx = state.steps.indexOf("pick-risers");
      if (value === "yes" && riserStepIdx < 0) {
        state.steps.splice(idx + 1, 0, "pick-risers");
      } else if (value === "no" && riserStepIdx >= 0) {
        state.steps.splice(riserStepIdx, 1);
        state.picks.risers = null;
      }
      advanceFrom(idx);
    }

    function pickRisersStep() {
      var pool = risers[state.answers.boardType];
      var banner = "Showing all " + pool.length + " riser pads we stock.";
      return renderPicks("Pick your riser pads", pool, banner, "pick-risers", "risers", function (card, r) {
        buildProductCardBody(card, r, "");
      }, "No riser pads in stock right now.");
    }

    function pickHardwareStep() {
      var a = state.answers;
      var pool = hardware[a.boardType];
      var range = cfg.hardwareLengthByRiser[a.riserPref];
      var list = pool.filter(function (h) { return h.length != null && h.length >= range[0] && h.length <= range[1]; });
      if (!list.length) list = pool;

      var banner = (a.riserPref === "yes"
        ? "Since you're running risers, showing hardware long enough to clear them"
        : "No risers, so showing standard-length hardware") + " - " + list.length + " matching.";

      var wrap = el("div", "sb-step sb-step--picker");
      wrap.appendChild(el("h3", "sb-step__title", "Pick your hardware"));
      var b = el("div", "sb-info-banner");
      b.innerHTML = banner;
      wrap.appendChild(b);

      if (list.length) {
        var grid = el("div", "sb-grid");
        list.forEach(function (product) {
          var selected = state.picks.hardware && state.picks.hardware.id === product.id;
          var card = el("div", "sb-card" + (selected ? " is-selected" : ""));
          buildProductCardBody(card, product, product.length ? product.length + "\u2033" : "");
          var btn = el("button", "sb-btn sb-btn--primary", selected ? "Selected" : "Select");
          btn.type = "button";
          btn.addEventListener("click", function () { state.picks.hardware = product; render(); });
          card.appendChild(btn);
          grid.appendChild(card);
        });
        wrap.appendChild(grid);
      } else {
        wrap.appendChild(el("p", "sb-empty", "No hardware in stock right now - call the shop to check."));
      }

      if (tool) {
        var toolGrid = el("div", "sb-addon-grid");
        toolGrid.appendChild(addonCard(tool, "tool", "handy for assembly and maintenance"));
        wrap.appendChild(toolGrid);
      }

      var next = el("button", "sb-btn sb-btn--primary", state.picks.hardware ? "Continue" : "Continue - I already have hardware");
      next.type = "button";
      next.addEventListener("click", function () { advanceFrom(state.steps.indexOf("pick-hardware")); });
      wrap.appendChild(next);
      return wrap;
    }

    function wheelFeelStep() {
      var a = state.answers;
      var q = a.boardType === "skateboard" ? cfg.wheelFollowupQuestions.skate : cfg.wheelFollowupQuestions[a.style];
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", q.label));
      var group = el("div", "sb-options");
      q.options.forEach(function (opt) {
        var current = (a.wheelFollowup || {})[q.key];
        var btn = el("button", "sb-option" + (current === opt.value ? " is-active" : ""), opt.label);
        btn.type = "button";
        btn.addEventListener("click", function () {
          var patch = {}; patch[q.key] = opt.value;
          confirmStep(state.steps.indexOf("wheel-feel"), { wheelFollowup: patch });
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    function recommendWheelSpec() {
      var a = state.answers;
      var base = cfg.wheelSpecsByStyle[a.style];
      var sMin = base.size[0], sMax = base.size[1], dMin = base.durometer[0], dMax = base.durometer[1];
      var wf = a.wheelFollowup || {};
      if (a.boardType === "skateboard" && wf.roughness === "rough") { sMax += 4; dMin = Math.max(75, dMin - 10); }
      if (wf.speedPriority === "speed") { sMin += 5; sMax += 5; }
      if (wf.speedPriority === "control") { sMax = Math.max(sMin, sMax - 5); }
      if (wf.trackType === "open") { sMin += 5; sMax += 5; }
      if (wf.trackType === "technical") { sMax = Math.max(sMin, sMax - 5); dMin += 2; dMax += 2; }
      if (wf.slideFeel === "slidey") { dMin += 4; dMax += 4; }
      if (wf.slideFeel === "grippy") { dMin = Math.max(75, dMin - 4); dMax -= 4; }
      return { size: [sMin, sMax], durometer: [dMin, dMax] };
    }

    function pickWheelsStep() {
      var a = state.answers;
      var spec = recommendWheelSpec();
      var pool = wheels[a.boardType];
      var list = pool.filter(function (w) { return w.size >= spec.size[0] && w.size <= spec.size[1] && w.durometer >= spec.durometer[0] && w.durometer <= spec.durometer[1]; });
      if (!list.length) list = pool;
      list.sort(function (x, y) { return x.size - y.size; });

      var banner = "Recommended range: <strong>" + spec.size[0] + "-" + spec.size[1] + "mm</strong>, <strong>" + spec.durometer[0] + "-" + spec.durometer[1] + "A</strong>. Showing all " + list.length + " matching wheels.";

      return renderPicks("Pick your wheels", list, banner, "pick-wheels", "wheels", function (card, w) {
        buildProductCardBody(card, w, w.size + "mm \u00B7 " + w.durometer + "A");
      }, "No wheels matched that range - showing the full range instead.");
    }

    // Tier to recommend is derived from riding style (see
    // bearingTierByStyle in skate-setup-builder-data.js) rather than
    // asking the customer to self-select a "budget" - that read as an odd
    // question mid-flow, and style already tells us how hard the bearings
    // will be worked. A speed-leaning wheel-feel answer can still bump a
    // style up a tier (ABEC ratings aren't a reliable signal for this -
    // load/speed is, which style + wheel priority already capture).
    function pickBearingsStep() {
      var a = state.answers;
      var tier = cfg.bearingTierByStyle[a.style] || "mid";
      var speedLeaning = (a.wheelFollowup || {}).speedPriority === "speed" || (a.wheelFollowup || {}).trackType === "open";
      var tierOrder = ["entry", "mid", "premium"];
      if (speedLeaning && tierOrder.indexOf(tier) < tierOrder.length - 1) tier = tierOrder[tierOrder.indexOf(tier) + 1];

      var list = bearings.filter(function (b) { return b.tier === tier; });
      if (!list.length) list = bearings;
      var banner = "For " + capitalize(a.style) + " riding, showing <strong>" + capitalize(tier) + "</strong> tier bearings - " + list.length + " matching.";
      return renderPicks("Pick your bearings", list, banner, "pick-bearings", "bearings", function (card, b) {
        buildProductCardBody(card, b, capitalize(b.tier) + " tier");
      }, "No bearings in that tier right now.");
    }

    function gripStep() {
      var f = el("div", "sb-step sb-step--picker");
      f.appendChild(el("h3", "sb-step__title", "Griptape"));
      f.appendChild(el("p", "sb-step__hint", "Standard clear grip already ships free with every deck - only pick something here if you want a printed upgrade."));

      var grid = el("div", "sb-grid");
      grip.forEach(function (g) {
        var card = el("div", "sb-card");
        buildProductCardBody(card, g, g.kind === "standard" ? "Included free" : "Upgrade");
        var btn = el("button", "sb-btn sb-btn--primary", g.kind === "standard" ? "Keep standard" : "Upgrade");
        btn.type = "button";
        btn.addEventListener("click", function () { confirmPick(state.steps.indexOf("grip"), "grip", g); });
        card.appendChild(btn);
        grid.appendChild(card);
      });
      f.appendChild(grid);

      // No featured "standard" product configured for this section instance
      // means grip has no free entry to fall back on - without this, a
      // customer who doesn't want a paid upgrade has no way off this step.
      if (!grip.some(function (g) { return g.kind === "standard"; })) {
        var skipBtn = el("button", "sb-btn sb-btn--secondary sb-skip-btn", "Skip - keep standard grip (free)");
        skipBtn.type = "button";
        skipBtn.addEventListener("click", function () { advanceFrom(state.steps.indexOf("grip")); });
        f.appendChild(skipBtn);
      }

      return f;
    }

    // Nothing in this builder blocks checkout - plenty of customers
    // already own some parts and just want to match new ones against
    // what they have, so every product step (not just the optional
    // add-ons) gets a skip. Also doubles as the escape hatch for an
    // empty collection, which used to be a dead end with no way forward.
    function renderPicks(title, list, banner, stepName, pickKey, buildBody, emptyMessage, skipQuestion) {
      var wrap = el("div", "sb-step sb-step--picker");
      wrap.appendChild(el("h3", "sb-step__title", title));
      if (banner) {
        var b = el("div", "sb-info-banner");
        b.innerHTML = banner;
        wrap.appendChild(b);
      }
      if (list.length) {
        var grid = el("div", "sb-grid");
        list.forEach(function (product) {
          var selected = state.picks[pickKey] && state.picks[pickKey].id === product.id;
          var card = el("div", "sb-card" + (selected ? " is-selected" : ""));
          buildBody(card, product);
          var btn = el("button", "sb-btn sb-btn--primary", selected ? "Selected" : "Select");
          btn.type = "button";
          btn.addEventListener("click", function () { confirmPick(state.steps.indexOf(stepName), pickKey, product); });
          card.appendChild(btn);
          grid.appendChild(card);
        });
        wrap.appendChild(grid);
      } else {
        wrap.appendChild(el("p", "sb-empty", emptyMessage));
      }

      // skipQuestion (deck step only, see pickDeckStep) - clicking skip
      // reveals a quick optional follow-up instead of immediately
      // advancing, so a downstream step that actually needs the info
      // (truck width matching) doesn't lose it entirely.
      if (skipQuestion && state.ui.skipPromptOpenFor === stepName) {
        var promptWrap = el("div", "sb-skip-prompt");
        promptWrap.appendChild(el("label", "sb-q-label", skipQuestion.label));
        var input = el("input", "sb-input");
        input.type = "number";
        input.step = "0.25";
        input.placeholder = skipQuestion.placeholder || "";
        promptWrap.appendChild(input);
        var confirmBtn = el("button", "sb-btn sb-btn--primary", "Continue");
        confirmBtn.type = "button";
        confirmBtn.addEventListener("click", function () {
          var val = parseFloat(input.value);
          skipQuestion.onConfirm(isNaN(val) ? null : val);
          advanceFrom(state.steps.indexOf(stepName));
        });
        promptWrap.appendChild(confirmBtn);
        wrap.appendChild(promptWrap);
      } else {
        var skipBtn = el("button", "sb-btn sb-btn--secondary sb-skip-btn", "Skip - I already have this");
        skipBtn.type = "button";
        skipBtn.addEventListener("click", function () {
          if (skipQuestion) { state.ui.skipPromptOpenFor = stepName; render(); }
          else { advanceFrom(state.steps.indexOf(stepName)); }
        });
        wrap.appendChild(skipBtn);
      }
      return wrap;
    }

    function buildProductCardBody(card, product, specText) {
      var link = document.createElement("a");
      link.className = "sb-card__link";
      link.href = product.url || "#";
      link.target = "_blank"; link.rel = "noopener";
      if (product.image) {
        var img = document.createElement("img");
        img.className = "sb-card__image"; img.src = product.image; img.alt = product.title;
        link.appendChild(img);
      }
      link.appendChild(el("h4", "sb-card__title", product.title));
      card.appendChild(link);
      card.appendChild(el("div", "sb-card__spec", specText || ""));
      card.appendChild(el("div", "sb-card__price", formatPrice(product.price)));
    }

    function renderBundle() {
      var wrap = el("div", "sb-results sb-bundle");
      wrap.appendChild(el("h3", "sb-step__title", "Your setup"));

      var rows = [
        ["Deck", state.picks.deck],
        ["Trucks", state.picks.trucks],
        ["Bushings", state.picks.bushings],
        ["Pivot cups", state.picks.pivotCups],
        ["Riser pads", state.picks.risers],
        ["Hardware", state.picks.hardware],
        ["Skate tool", state.picks.tool],
        ["Wheels", state.picks.wheels],
        ["Bearings", state.picks.bearings],
        ["Griptape", state.picks.grip]
      ];
      var picked = rows.filter(function (r) { return r[1]; });
      var total = 0;
      if (picked.length) {
        var list = el("div", "sb-bundle__list");
        picked.forEach(function (r) {
          total += r[1].price;
          var row = el("div", "sb-bundle__row");
          row.appendChild(el("span", "sb-bundle__row-label", r[0]));
          row.appendChild(el("span", "sb-bundle__row-value", r[1].title + " - " + formatPrice(r[1].price)));
          list.appendChild(row);
        });
        wrap.appendChild(list);

        var totalRow = el("div", "sb-bundle__total");
        totalRow.appendChild(el("span", "", "Total"));
        totalRow.appendChild(el("span", "", formatPrice(total)));
        wrap.appendChild(totalRow);
      } else {
        wrap.appendChild(el("p", "sb-empty", "You skipped everything - go back to pick a part, or just browse the store."));
      }

      // Nothing is required to check out - plenty of customers already
      // own some parts and are just here to match new pieces to what
      // they have. Only guard against adding a literal empty order.
      var addAll = el("button", "sb-btn sb-btn--primary sb-bundle__add-all", "Add all to cart");
      addAll.type = "button";
      addAll.disabled = !picked.length;
      addAll.addEventListener("click", function () { addAllToCart(); });
      wrap.appendChild(addAll);
      return wrap;
    }

    function addAllToCart() {
      var items = [];
      ["deck", "trucks", "bushings", "pivotCups", "risers", "hardware", "tool", "wheels", "bearings", "grip"].forEach(function (k) {
        var p = state.picks[k];
        // Standard grip already ships free with every deck - it's not a
        // real checkout line item, so it's never added to the cart even
        // though it's tracked as a "pick" for the summary/bundle display.
        if (p && p.variantId && p.kind !== "standard") items.push({ id: p.variantId, quantity: 1 });
      });
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items })
      }).then(function () {
        document.dispatchEvent(new CustomEvent("skate-setup-builder:added-to-cart"));
      });
    }

    render();
  }

  document.addEventListener("DOMContentLoaded", function () {
    var roots = document.querySelectorAll("[data-skate-setup-builder]");
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  });
})();
